/**
 * Rax Agent Brain Component
 *
 * Unified view combining agent configuration, memory, learning ledger,
 * and activity timeline into a single cohesive interface.
 *
 * Features:
 * - Tabbed or scrollable unified view
 * - Config editor integration
 * - Learning ledger display
 * - Activity heatmap
 * - Quality metrics (Alignment/Autonomy/Safety/Clarity)
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme, raxButtonStyles, raxCardStyles } from "../styles/rax-theme.js";
import "./activity-heatmap.js";
import "./learning-ledger.js";
import "./quality-dashboard.js";
import "./agent-config-editor.js";

type BrainTab = "overview" | "config" | "memory" | "ledger" | "timeline";

interface AgentMemoryEntry {
  id: string;
  content: string;
  type: "fact" | "preference" | "lesson" | "context";
  importance: number;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
  accessCount: number;
}

interface AgentMemoryStats {
  totalEntries: number;
  byType: Record<string, number>;
  storageUsedBytes: number;
  oldestEntry?: string;
  newestEntry?: string;
}

interface TimelineEvent {
  id: string;
  type: "message" | "action" | "learning" | "config_change" | "error";
  title: string;
  description?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

@customElement("rax-agent-brain")
export class RaxAgentBrain extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    raxCardStyles,
    css`
      :host {
        display: block;
        height: 100%;
        overflow: hidden;
      }

      .brain-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: var(--bg-primary);
      }

      /* Tab Navigation */
      .brain-tabs {
        display: flex;
        gap: 2px;
        padding: var(--space-2) var(--space-4);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
        overflow-x: auto;
        flex-shrink: 0;
      }

      .brain-tabs::-webkit-scrollbar {
        height: 4px;
      }

      .brain-tabs::-webkit-scrollbar-thumb {
        background: var(--border-color);
        border-radius: 2px;
      }

      .brain-tab {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        background: transparent;
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .brain-tab:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }

      .brain-tab.active {
        background: rgba(255, 208, 0, 0.1);
        border-color: rgba(255, 208, 0, 0.3);
        color: var(--rax-yellow);
      }

      .brain-tab-icon {
        font-size: 16px;
      }

      .brain-tab-badge {
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        background: var(--bg-primary);
        color: var(--text-tertiary);
        font-weight: 600;
      }

      .brain-tab.active .brain-tab-badge {
        background: rgba(255, 208, 0, 0.2);
        color: var(--rax-yellow);
      }

      /* Content Area */
      .brain-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
      }

      /* Overview Tab */
      .overview-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: var(--space-4);
        padding: var(--space-4);
      }

      .overview-section {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }

      .overview-section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
      }

      .overview-section-title {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }

      .overview-section-action {
        font-size: var(--font-xs);
        color: var(--rax-yellow);
        text-decoration: none;
        cursor: pointer;
        transition: opacity var(--transition-fast);
      }

      .overview-section-action:hover {
        opacity: 0.8;
      }

      .overview-section-content {
        padding: var(--space-4);
      }

      /* Quality Metrics Mini */
      .quality-mini {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
      }

      .quality-metric {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .quality-metric-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .quality-metric-name {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }

      .quality-metric-value {
        font-size: var(--font-sm);
        font-weight: 700;
        color: var(--text-primary);
      }

      .quality-metric-bar {
        height: 4px;
        background: var(--bg-primary);
        border-radius: 2px;
        overflow: hidden;
      }

      .quality-metric-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease;
      }

      .quality-metric-fill.high {
        background: var(--success);
      }

      .quality-metric-fill.medium {
        background: var(--rax-yellow);
      }

      .quality-metric-fill.low {
        background: var(--error);
      }

      .quality-grade-large {
        text-align: center;
        padding: var(--space-2);
      }

      .quality-grade-circle {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-amber));
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto var(--space-2);
        box-shadow: 0 0 16px rgba(255, 208, 0, 0.25);
      }

      .quality-grade-letter {
        font-size: 24px;
        font-weight: 800;
        color: var(--rax-black);
      }

      .quality-grade-label {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }

      /* Memory Summary */
      .memory-summary {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .memory-stat-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: var(--font-sm);
      }

      .memory-stat-label {
        color: var(--text-secondary);
      }

      .memory-stat-value {
        font-weight: 600;
        color: var(--text-primary);
      }

      .memory-type-pills {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
      }

      .memory-type-pill {
        font-size: 10px;
        padding: 2px 8px;
        border-radius: var(--radius-full);
        background: var(--bg-primary);
        color: var(--text-secondary);
      }

      /* Recent Learnings */
      .recent-learnings {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .learning-item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        padding: var(--space-2);
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
      }

      .learning-icon {
        flex-shrink: 0;
        font-size: 14px;
      }

      .learning-content {
        flex: 1;
        min-width: 0;
      }

      .learning-key {
        font-weight: 600;
        color: var(--text-primary);
      }

      .learning-value {
        color: var(--text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Timeline Mini */
      .timeline-mini {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .timeline-event {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        padding: var(--space-2);
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
      }

      .timeline-event-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-top: 4px;
        flex-shrink: 0;
      }

      .timeline-event-dot.message {
        background: var(--rax-yellow);
      }

      .timeline-event-dot.action {
        background: var(--info);
      }

      .timeline-event-dot.learning {
        background: var(--success);
      }

      .timeline-event-dot.config_change {
        background: #a78bfa;
      }

      .timeline-event-dot.error {
        background: var(--error);
      }

      .timeline-event-content {
        flex: 1;
        min-width: 0;
      }

      .timeline-event-title {
        color: var(--text-primary);
        font-weight: 500;
      }

      .timeline-event-time {
        color: var(--text-tertiary);
        font-family: ui-monospace, monospace;
        font-size: 10px;
      }

      /* Full Tab Content */
      .tab-content-full {
        height: 100%;
      }

      /* Memory Tab */
      .memory-content {
        padding: var(--space-4);
      }

      .memory-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .memory-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }

      .memory-stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--space-3);
        margin-bottom: var(--space-5);
      }

      .memory-stat-card {
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        text-align: center;
      }

      .memory-stat-card-value {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--rax-yellow);
      }

      .memory-stat-card-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: 2px;
      }

      .memory-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .memory-entry {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }

      .memory-entry-icon {
        font-size: 18px;
        flex-shrink: 0;
      }

      .memory-entry-content {
        flex: 1;
        min-width: 0;
      }

      .memory-entry-text {
        font-size: var(--font-sm);
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }

      .memory-entry-meta {
        display: flex;
        gap: var(--space-3);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      /* Timeline Tab */
      .timeline-content {
        padding: var(--space-4);
      }

      .timeline-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .timeline-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }

      .timeline-filter-bar {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
        flex-wrap: wrap;
      }

      .timeline-filter-chip {
        padding: var(--space-1) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .timeline-filter-chip:hover {
        border-color: var(--text-tertiary);
      }

      .timeline-filter-chip.active {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: var(--rax-yellow);
        font-weight: 600;
      }

      .timeline-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .timeline-entry {
        display: flex;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }

      .timeline-entry-indicator {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 24px;
        flex-shrink: 0;
      }

      .timeline-entry-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
      }

      .timeline-entry-icon.message {
        background: rgba(255, 208, 0, 0.2);
        color: var(--rax-yellow);
      }

      .timeline-entry-icon.action {
        background: rgba(59, 130, 246, 0.2);
        color: var(--info);
      }

      .timeline-entry-icon.learning {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }

      .timeline-entry-icon.config_change {
        background: rgba(167, 139, 250, 0.2);
        color: #a78bfa;
      }

      .timeline-entry-icon.error {
        background: rgba(239, 68, 68, 0.2);
        color: var(--error);
      }

      .timeline-entry-line {
        width: 2px;
        flex: 1;
        background: var(--border-color);
        margin-top: var(--space-1);
      }

      .timeline-entry-body {
        flex: 1;
        min-width: 0;
      }

      .timeline-entry-title {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 2px;
      }

      .timeline-entry-desc {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-bottom: var(--space-1);
      }

      .timeline-entry-time {
        font-size: 10px;
        color: var(--text-tertiary);
        font-family: ui-monospace, monospace;
      }

      /* Loading and Empty States */
      .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      .empty-state-icon {
        font-size: 40px;
        margin-bottom: var(--space-3);
        opacity: 0.5;
      }

      /* Responsive */
      @media (max-width: 768px) {
        .brain-tabs {
          padding: var(--space-2);
        }

        .brain-tab {
          padding: var(--space-2) var(--space-3);
          font-size: var(--font-xs);
        }

        .overview-grid {
          grid-template-columns: 1fr;
          padding: var(--space-3);
        }
      }
    `,
  ];

  @property({ type: String })
  agentId: string | null = null;

  @state()
  private activeTab: BrainTab = "overview";

  @state()
  private loading = true;

  @state()
  private memoryStats: AgentMemoryStats | null = null;

  @state()
  private memoryEntries: AgentMemoryEntry[] = [];

  @state()
  private qualityMetrics: {
    alignment: number;
    autonomy: number;
    safety: number;
    clarity: number;
    grade: string;
  } | null = null;

  @state()
  private recentLearnings: Array<{ key: string; value: string; type: string }> = [];

  @state()
  private timelineEvents: TimelineEvent[] = [];

  @state()
  private timelineFilter: string | null = null;

  @state()
  private ledgerCount = 0;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadOverviewData();
  }

  private async loadOverviewData() {
    this.loading = true;
    try {
      // Load all overview data in parallel
      const [qualityRes, memoryRes, ledgerRes, timelineRes] = await Promise.all([
        raxApi.request<{
          metrics: {
            alignment: number;
            autonomy: number;
            safety: number;
            clarity: number;
            grade: string;
          };
        }>("GET", "/api/rax/quality"),
        raxApi.request<{
          stats: AgentMemoryStats;
          entries: AgentMemoryEntry[];
        }>("GET", "/api/rax/memory?limit=5"),
        raxApi.request<{ count: number; recent: Array<{ key: string; value: string; type: string }> }>(
          "GET",
          "/api/rax/ledger/summary"
        ),
        raxApi.request<TimelineEvent[]>("GET", "/api/rax/timeline?limit=10"),
      ]);

      if (qualityRes.success && qualityRes.data?.metrics) {
        this.qualityMetrics = qualityRes.data.metrics;
      }

      if (memoryRes.success && memoryRes.data) {
        this.memoryStats = memoryRes.data.stats;
        this.memoryEntries = memoryRes.data.entries;
      }

      if (ledgerRes.success && ledgerRes.data) {
        this.ledgerCount = ledgerRes.data.count;
        this.recentLearnings = ledgerRes.data.recent || [];
      }

      if (timelineRes.success && timelineRes.data) {
        this.timelineEvents = timelineRes.data;
      }
    } catch (err) {
      console.error("[AgentBrain] Failed to load overview data:", err);
    } finally {
      this.loading = false;
    }
  }

  private async loadMemory() {
    try {
      const res = await raxApi.request<{
        stats: AgentMemoryStats;
        entries: AgentMemoryEntry[];
      }>("GET", "/api/rax/memory");
      if (res.success && res.data) {
        this.memoryStats = res.data.stats;
        this.memoryEntries = res.data.entries;
      }
    } catch (err) {
      console.error("[AgentBrain] Failed to load memory:", err);
    }
  }

  private async loadTimeline() {
    try {
      const url = this.timelineFilter
        ? `/api/rax/timeline?type=${encodeURIComponent(this.timelineFilter)}`
        : "/api/rax/timeline";
      const res = await raxApi.request<TimelineEvent[]>("GET", url);
      if (res.success && res.data) {
        this.timelineEvents = res.data;
      }
    } catch (err) {
      console.error("[AgentBrain] Failed to load timeline:", err);
    }
  }

  private setTab(tab: BrainTab) {
    this.activeTab = tab;
    if (tab === "memory") {
      this.loadMemory();
    } else if (tab === "timeline") {
      this.loadTimeline();
    }
  }

  private setTimelineFilter(filter: string | null) {
    this.timelineFilter = filter;
    this.loadTimeline();
  }

  private getScoreClass(score: number): string {
    if (score >= 80) return "high";
    if (score >= 50) return "medium";
    return "low";
  }

  private getMemoryIcon(type: string): string {
    const icons: Record<string, string> = {
      fact: "📋",
      preference: "⚙️",
      lesson: "💡",
      context: "🔍",
    };
    return icons[type] || "📝";
  }

  private getTimelineIcon(type: string): string {
    const icons: Record<string, string> = {
      message: "💬",
      action: "⚡",
      learning: "🧠",
      config_change: "⚙️",
      error: "⚠️",
    };
    return icons[type] || "📋";
  }

  private formatTime(isoStr: string): string {
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  override render() {
    return html`
      <div class="brain-container">
        <div class="brain-tabs">
          <button
            class="brain-tab ${this.activeTab === "overview" ? "active" : ""}"
            @click=${() => this.setTab("overview")}
          >
            <span class="brain-tab-icon">🧠</span>
            <span>Overview</span>
          </button>
          <button
            class="brain-tab ${this.activeTab === "config" ? "active" : ""}"
            @click=${() => this.setTab("config")}
          >
            <span class="brain-tab-icon">⚙️</span>
            <span>Config</span>
          </button>
          <button
            class="brain-tab ${this.activeTab === "memory" ? "active" : ""}"
            @click=${() => this.setTab("memory")}
          >
            <span class="brain-tab-icon">💾</span>
            <span>Memory</span>
            ${this.memoryStats ? html`
              <span class="brain-tab-badge">${this.memoryStats.totalEntries}</span>
            ` : ""}
          </button>
          <button
            class="brain-tab ${this.activeTab === "ledger" ? "active" : ""}"
            @click=${() => this.setTab("ledger")}
          >
            <span class="brain-tab-icon">📚</span>
            <span>Ledger</span>
            ${this.ledgerCount > 0 ? html`
              <span class="brain-tab-badge">${this.ledgerCount}</span>
            ` : ""}
          </button>
          <button
            class="brain-tab ${this.activeTab === "timeline" ? "active" : ""}"
            @click=${() => this.setTab("timeline")}
          >
            <span class="brain-tab-icon">📊</span>
            <span>Timeline</span>
          </button>
        </div>

        <div class="brain-content">
          ${this.renderTabContent()}
        </div>
      </div>
    `;
  }

  private renderTabContent() {
    switch (this.activeTab) {
      case "overview":
        return this.renderOverview();
      case "config":
        return html`<rax-agent-config-editor class="tab-content-full"></rax-agent-config-editor>`;
      case "memory":
        return this.renderMemory();
      case "ledger":
        return html`<rax-learning-ledger class="tab-content-full"></rax-learning-ledger>`;
      case "timeline":
        return this.renderTimeline();
      default:
        return nothing;
    }
  }

  private renderOverview() {
    if (this.loading) {
      return html`<div class="loading">Loading agent brain...</div>`;
    }

    return html`
      <div class="overview-grid">
        <!-- Quality Metrics -->
        <div class="overview-section">
          <div class="overview-section-header">
            <div class="overview-section-title">
              <span>📊</span>
              <span>Quality</span>
            </div>
            <span class="overview-section-action" @click=${() => this.setTab("ledger")}>
              View details
            </span>
          </div>
          <div class="overview-section-content">
            ${this.qualityMetrics ? this.renderQualityMini() : html`
              <div class="empty-state">
                <div>Not enough data yet</div>
              </div>
            `}
          </div>
        </div>

        <!-- Activity Heatmap -->
        <div class="overview-section" style="grid-column: span 2;">
          <div class="overview-section-header">
            <div class="overview-section-title">
              <span>📈</span>
              <span>Activity</span>
            </div>
            <span class="overview-section-action" @click=${() => this.setTab("timeline")}>
              View timeline
            </span>
          </div>
          <div class="overview-section-content" style="padding: 0;">
            <rax-activity-heatmap .weeks=${8}></rax-activity-heatmap>
          </div>
        </div>

        <!-- Memory Summary -->
        <div class="overview-section">
          <div class="overview-section-header">
            <div class="overview-section-title">
              <span>💾</span>
              <span>Memory</span>
            </div>
            <span class="overview-section-action" @click=${() => this.setTab("memory")}>
              Manage
            </span>
          </div>
          <div class="overview-section-content">
            ${this.memoryStats ? this.renderMemorySummary() : html`
              <div class="empty-state">
                <div>No memories yet</div>
              </div>
            `}
          </div>
        </div>

        <!-- Recent Learnings -->
        <div class="overview-section">
          <div class="overview-section-header">
            <div class="overview-section-title">
              <span>🧠</span>
              <span>Recent Learnings</span>
            </div>
            <span class="overview-section-action" @click=${() => this.setTab("ledger")}>
              View all
            </span>
          </div>
          <div class="overview-section-content">
            ${this.recentLearnings.length > 0 ? this.renderRecentLearnings() : html`
              <div class="empty-state">
                <div>No learnings yet</div>
              </div>
            `}
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="overview-section">
          <div class="overview-section-header">
            <div class="overview-section-title">
              <span>⚡</span>
              <span>Recent Activity</span>
            </div>
            <span class="overview-section-action" @click=${() => this.setTab("timeline")}>
              View all
            </span>
          </div>
          <div class="overview-section-content">
            ${this.timelineEvents.length > 0 ? this.renderTimelineMini() : html`
              <div class="empty-state">
                <div>No activity yet</div>
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private renderQualityMini() {
    if (!this.qualityMetrics) return nothing;

    const { alignment, autonomy, safety, clarity, grade } = this.qualityMetrics;

    return html`
      <div style="display: flex; gap: var(--space-4); align-items: center;">
        <div class="quality-grade-large">
          <div class="quality-grade-circle">
            <span class="quality-grade-letter">${grade}</span>
          </div>
          <div class="quality-grade-label">Overall</div>
        </div>
        <div class="quality-mini" style="flex: 1;">
          <div class="quality-metric">
            <div class="quality-metric-header">
              <span class="quality-metric-name">Alignment</span>
              <span class="quality-metric-value">${alignment}%</span>
            </div>
            <div class="quality-metric-bar">
              <div
                class="quality-metric-fill ${this.getScoreClass(alignment)}"
                style="width: ${alignment}%"
              ></div>
            </div>
          </div>
          <div class="quality-metric">
            <div class="quality-metric-header">
              <span class="quality-metric-name">Autonomy</span>
              <span class="quality-metric-value">${autonomy}%</span>
            </div>
            <div class="quality-metric-bar">
              <div
                class="quality-metric-fill ${this.getScoreClass(autonomy)}"
                style="width: ${autonomy}%"
              ></div>
            </div>
          </div>
          <div class="quality-metric">
            <div class="quality-metric-header">
              <span class="quality-metric-name">Safety</span>
              <span class="quality-metric-value">${safety}%</span>
            </div>
            <div class="quality-metric-bar">
              <div
                class="quality-metric-fill ${this.getScoreClass(safety)}"
                style="width: ${safety}%"
              ></div>
            </div>
          </div>
          <div class="quality-metric">
            <div class="quality-metric-header">
              <span class="quality-metric-name">Clarity</span>
              <span class="quality-metric-value">${clarity}%</span>
            </div>
            <div class="quality-metric-bar">
              <div
                class="quality-metric-fill ${this.getScoreClass(clarity)}"
                style="width: ${clarity}%"
              ></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderMemorySummary() {
    if (!this.memoryStats) return nothing;

    return html`
      <div class="memory-summary">
        <div class="memory-stat-row">
          <span class="memory-stat-label">Total entries</span>
          <span class="memory-stat-value">${this.memoryStats.totalEntries}</span>
        </div>
        <div class="memory-stat-row">
          <span class="memory-stat-label">Storage used</span>
          <span class="memory-stat-value">${this.formatBytes(this.memoryStats.storageUsedBytes)}</span>
        </div>
        <div class="memory-type-pills">
          ${Object.entries(this.memoryStats.byType).map(
            ([type, count]) => html`
              <span class="memory-type-pill">${type}: ${count}</span>
            `
          )}
        </div>
      </div>
    `;
  }

  private renderRecentLearnings() {
    return html`
      <div class="recent-learnings">
        ${this.recentLearnings.slice(0, 4).map(
          (item) => html`
            <div class="learning-item">
              <span class="learning-icon">${this.getLearningIcon(item.type)}</span>
              <div class="learning-content">
                <span class="learning-key">${item.key}</span>
                <div class="learning-value">${item.value}</div>
              </div>
            </div>
          `
        )}
      </div>
    `;
  }

  private getLearningIcon(type: string): string {
    const icons: Record<string, string> = {
      personal_info: "👤",
      preference: "⚙️",
      vocabulary: "📝",
      workflow: "🔄",
      correction: "✏️",
      relationship: "👥",
      schedule: "📅",
      tool_preference: "🔧",
    };
    return icons[type] || "📋";
  }

  private renderTimelineMini() {
    return html`
      <div class="timeline-mini">
        ${this.timelineEvents.slice(0, 5).map(
          (event) => html`
            <div class="timeline-event">
              <span class="timeline-event-dot ${event.type}"></span>
              <div class="timeline-event-content">
                <span class="timeline-event-title">${event.title}</span>
                <span class="timeline-event-time">${this.formatTime(event.timestamp)}</span>
              </div>
            </div>
          `
        )}
      </div>
    `;
  }

  private renderMemory() {
    return html`
      <div class="memory-content">
        <div class="memory-header">
          <h2 class="memory-title">Agent Memory</h2>
          <button class="btn btn-secondary" @click=${() => this.loadMemory()}>
            Refresh
          </button>
        </div>

        ${this.memoryStats ? html`
          <div class="memory-stats-grid">
            <div class="memory-stat-card">
              <div class="memory-stat-card-value">${this.memoryStats.totalEntries}</div>
              <div class="memory-stat-card-label">Total Memories</div>
            </div>
            <div class="memory-stat-card">
              <div class="memory-stat-card-value">${this.formatBytes(this.memoryStats.storageUsedBytes)}</div>
              <div class="memory-stat-card-label">Storage Used</div>
            </div>
            <div class="memory-stat-card">
              <div class="memory-stat-card-value">${Object.keys(this.memoryStats.byType).length}</div>
              <div class="memory-stat-card-label">Categories</div>
            </div>
          </div>
        ` : ""}

        ${this.memoryEntries.length > 0 ? html`
          <div class="memory-list">
            ${this.memoryEntries.map(
              (entry) => html`
                <div class="memory-entry">
                  <span class="memory-entry-icon">${this.getMemoryIcon(entry.type)}</span>
                  <div class="memory-entry-content">
                    <div class="memory-entry-text">${entry.content}</div>
                    <div class="memory-entry-meta">
                      <span>${entry.type}</span>
                      <span>Importance: ${entry.importance}</span>
                      <span>Accessed ${entry.accessCount}x</span>
                      <span>${this.formatTime(entry.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              `
            )}
          </div>
        ` : html`
          <div class="empty-state">
            <div class="empty-state-icon">💾</div>
            <div>No memories stored yet</div>
            <p style="margin-top: var(--space-2); font-size: var(--font-sm);">
              Your agent will accumulate memories as you interact with it.
            </p>
          </div>
        `}
      </div>
    `;
  }

  private renderTimeline() {
    const eventTypes = [...new Set(this.timelineEvents.map((e) => e.type))];

    return html`
      <div class="timeline-content">
        <div class="timeline-header">
          <h2 class="timeline-title">Activity Timeline</h2>
          <button class="btn btn-secondary" @click=${() => this.loadTimeline()}>
            Refresh
          </button>
        </div>

        <div class="timeline-filter-bar">
          <span
            class="timeline-filter-chip ${!this.timelineFilter ? "active" : ""}"
            @click=${() => this.setTimelineFilter(null)}
          >All</span>
          ${eventTypes.map(
            (type) => html`
              <span
                class="timeline-filter-chip ${this.timelineFilter === type ? "active" : ""}"
                @click=${() => this.setTimelineFilter(type)}
              >${this.getTimelineIcon(type)} ${type.replace("_", " ")}</span>
            `
          )}
        </div>

        ${this.timelineEvents.length > 0 ? html`
          <div class="timeline-list">
            ${this.timelineEvents.map(
              (event, i) => html`
                <div class="timeline-entry">
                  <div class="timeline-entry-indicator">
                    <span class="timeline-entry-icon ${event.type}">
                      ${this.getTimelineIcon(event.type)}
                    </span>
                    ${i < this.timelineEvents.length - 1 ? html`
                      <div class="timeline-entry-line"></div>
                    ` : ""}
                  </div>
                  <div class="timeline-entry-body">
                    <div class="timeline-entry-title">${event.title}</div>
                    ${event.description ? html`
                      <div class="timeline-entry-desc">${event.description}</div>
                    ` : ""}
                    <div class="timeline-entry-time">${this.formatTime(event.timestamp)}</div>
                  </div>
                </div>
              `
            )}
          </div>
        ` : html`
          <div class="empty-state">
            <div class="empty-state-icon">📊</div>
            <div>No activity yet</div>
            <p style="margin-top: var(--space-2); font-size: var(--font-sm);">
              Activity events will appear here as you use your agent.
            </p>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-agent-brain": RaxAgentBrain;
  }
}
